import { useState, useEffect } from 'react'
import { getOrderLimits, requestLimitIncrease } from 'src/common/api'
import { getOrganizationProfile } from 'src/common/api/organizations'
import { useAuth } from 'src/common/context'
import { BlockchainDetailsModel } from 'src/common/api/organizations/models'
import { WalletModel } from 'src/common/api/wallets/models'
import { alertDialog, alertNotification } from 'src/ui'

const useProfile = () => {
  const { user } = useAuth()
  const [orgBlockchainDetails, setOrgBlockchainDetails] = useState<BlockchainDetailsModel[]>([])
  const [displayWallets, setDisplayWallets] = useState<WalletModel[]>([])
  const [organization, setOrganization] = useState<{ orgName: string; email: string } | null>(null)

  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [availableLimits, setAvailableLimits] = useState({
    daily: 100000,
    weekly: 500000
  })
  const [blockchainIdx, setBlockchainIdx] = useState(0)
  const [showQR, setShowQR] = useState(false)

  const sendIncreaseLimitRequest = async () => {
    setSubmitting(true)
    try {
      await requestLimitIncrease()
      setSubmitting(false)
      setOpen(false)
      alertDialog(
        'Request has been sent.',
        'We will get back to you once we review your eligibility. We appreciate your patience.'
      )
    } catch (err) {
      setOpen(false)
      alertNotification('Sending request to increase limit failed. Please contact support.', 'error')
    }
  }

  const fetchOrganizationBlockchainDetails = async () => {
    try {
      const response = await getOrganizationProfile()
      const orgDetails = response.data.organizationDetails
      const blockchainDetails = response.data.blockchainDetails || []

      setOrganization({
        orgName: orgDetails.orgName,
        email: orgDetails.email
      })

      setOrgBlockchainDetails(blockchainDetails)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transformedWallets: WalletModel[] = blockchainDetails.map((detail: any) => ({
        id: detail.id,
        address: detail.address,
        name: detail.name,
        chain: detail.chain
      }))
      setDisplayWallets(transformedWallets)
    } catch (err) {
      console.error('Failed to fetch organization blockchain details:', err)
    }
  }

  const getDetails = async () => {
    try {
      await fetchOrganizationBlockchainDetails()
    } catch (err) {
      alertNotification('Fetching organization details failed. Please contact support.', 'error')
    }
  }

  const getAvailableLimits = async () => {
    try {
      const response = await getOrderLimits()
      setAvailableLimits({
        daily: response.data.dailyBankingLimit,
        weekly: response.data.weeklyBankingLimit
      })
    } catch (err) {
      alertNotification('Fetching available limits failed. Please contact support.', 'error')
    }
  }

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value)
    alertNotification('Successfully copied to clipboard!', 'success')
  }

  const openQR = (idx: number) => {
    setBlockchainIdx(idx)
    setShowQR(true)
  }

  const closeQR = () => {
    setShowQR(false)
  }

  useEffect(() => {
    getDetails()
    getAvailableLimits()
  }, [])

  useEffect(() => {
    fetchOrganizationBlockchainDetails()
  }, [user?.organization])

  return {
    user,
    organization,
    wallets: displayWallets,
    open,
    submitting,
    availableLimits,
    blockchainIdx,
    showQR,
    orgBlockchainDetails,
    sendIncreaseLimitRequest,
    copyToClipboard,
    openQR,
    closeQR
  }
}

export default useProfile
